/**
 * Auth Layout Route
 *
 * Layout route for authentication pages (login, register, etc.)
 * Uses pathless layout pattern (underscore prefix).
 * Redirects to dashboard if user is already authenticated.
 *
 * Uses two-layer auth guard:
 * 1. beforeLoad: catches authenticated access on subsequent navigations
 * 2. Component-level guard: catches the initial load race condition where
 *    beforeLoad returns early during auth loading, then redirects once
 *    auth state resolves via useAuth() subscription
 */

import { useEffect } from "react";
import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { AuthLayout } from "../components/layout/AuthLayout";
import { useAuth } from "../stores/authStore";
import { safeNext, isExternalNext } from "../lib/safe-next";
import { useNoIndex } from "../hooks/useSEO";

export const Route = createFileRoute("/_auth")({
  beforeLoad: ({ context, location }) => {
    const { auth } = context;

    // If auth is resolved and user is authenticated, redirect immediately.
    // This handles subsequent navigations where context is already up-to-date.
    if (!auth.isLoading && auth.isAuthenticated) {
      // 29-24: an already-logged-in user who followed the MCP consent's login
      // link (/login?next=<authorize url>) should resume the connection, not
      // land in the app. Only a SAFE external next qualifies for the hard
      // redirect; everything else keeps the /clients default.
      const next = safeNext(new URLSearchParams(location.searchStr).get("next"));
      if (next && isExternalNext(next)) {
        throw redirect({ href: next });
      }
      throw redirect({ to: "/clients" });
    }

    // If still loading, let the component handle the guard.
  },
  component: AuthLayoutComponent,
});

function AuthLayoutComponent() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Auth flow pages (login, register, reset, etc.) are noindex,nofollow
  // (Story 30.2) — applied at the layout so no auth route can forget it.
  useNoIndex();

  // Component-level guard: redirect when auth resolves to authenticated.
  // This catches the race condition where beforeLoad returns early during loading.
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const next = safeNext(new URLSearchParams(window.location.search).get("next"));
      if (next && isExternalNext(next)) {
        window.location.assign(next);
        return;
      }
      navigate({ to: "/clients" });
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Don't render auth pages while redirect is in progress
  if (!isLoading && isAuthenticated) {
    return null;
  }

  return (
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  );
}
